import * as React from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import TableContainer from "@mui/material/TableContainer";
import Typography from "@mui/material/Typography";

import { DataTable, ColumnDef } from "../../../../data-table";
import type { QueryField, QueryResults } from "../types";
import { FullscreenTable } from "./fullscreen-table";
import { SmallPagination } from "./small-pagination";

export interface ResultsTableProps {
	/** Query results data */
	results: QueryResults;
	/** Query fields for column configuration */
	queryFields?: QueryField[];
	/** Initial page size */
	pageSize?: number;
	/** Hide internal pagination (for fullscreen use) */
	hideInternalPagination?: boolean;
	/** Loading state for initial data load */
	loading?: boolean;
	/** External fullscreen state control */
	fullscreenOpen?: boolean;
	/** Callback when fullscreen state should change */
	onFullscreenChange?: (open: boolean) => void;
	page?: number;
	totalPages?: number;
	onPageChange?: (page: number) => void;
	/** Callback when a row is clicked for feature highlighting */
	onRowClick?: (row: any) => void;
	/** Currently selected row for highlighting */
	selectedRowId?: number | string;
	/** Array of column names to exclude from results table */
	excludeColumns?: string[];
}

export function ResultsTable({
	results,
	queryFields = [],
	hideInternalPagination = false,
	loading = false,
	fullscreenOpen: externalFullscreenOpen,
	onFullscreenChange,
	page,
	totalPages,
	onPageChange,
	onRowClick,
	selectedRowId,
	excludeColumns = [],
}: ResultsTableProps): React.JSX.Element {
	const [internalFullscreenOpen, setInternalFullscreenOpen] =
		React.useState(false);

	// Use external fullscreen state if provided, otherwise use internal state
	const fullscreenOpen = externalFullscreenOpen ?? internalFullscreenOpen;
	const setFullscreenOpen = onFullscreenChange ?? setInternalFullscreenOpen;

	// const getColumnWidth = (field: QueryField): string => {
	// 	switch (field.type) {
	// 		case "text": {
	// 			return field.options && field.options.length > 0 ? "150px" : "200px";
	// 		}
	// 		case "numeric": {
	// 			return "120px";
	// 		}
	// 		case "date": {
	// 			return "140px";
	// 		}
	// 		default: {
	// 			return "150px";
	// 		}
	// 	}
	// };

	// const getColumnFormatter = (field: QueryField) => {
	// 	return (row: any): React.ReactNode => {
	// 		const value = row[field.name];

	// 		if (value === null || value === undefined) {
	// 			return (
	// 				<Typography
	// 					variant="body2"
	// 					color="text.disabled">
	// 					—
	// 				</Typography>
	// 			);
	// 		}

	// 		switch (field.type) {
	// 			case "numeric": {
	// 				if (typeof value === "number") {
	// 					return (
	// 						<Typography
	// 							variant="body2"
	// 							sx={{ fontFamily: "monospace" }}>
	// 							{value.toLocaleString()}
	// 						</Typography>
	// 					);
	// 				}
	// 				break;
	// 			}
	// 			case "date": {
	// 				if (typeof value === "string") {
	// 					const date = new Date(value);
	// 					if (!isNaN(date.getTime())) {
	// 						return (
	// 							<Typography variant="body2">
	// 								{date.toLocaleDateString("en-US", {
	// 									year: "numeric",
	// 									month: "short",
	// 									day: "numeric",
	// 								})}
	// 							</Typography>
	// 						);
	// 					}
	// 				}
	// 				break;
	// 			}
	// 			case "text":
	// 			default: {
	// 				return (
	// 					<Typography
	// 						variant="body2"
	// 						sx={{
	// 							overflow: "hidden",
	// 							textOverflow: "ellipsis",
	// 							whiteSpace: "nowrap",
	// 						}}>
	// 						{String(value)}
	// 					</Typography>
	// 				);
	// 			}
	// 		}

	// 		return <Typography variant="body2">{String(value)}</Typography>;
	// 	};
	// };

	const formatColumnName = (key: string): string => {
		// Convert snake_case or camelCase to Title Case
		return key
			.replaceAll(/([A-Z])/g, " $1")
			.replaceAll("_", " ")
			.replaceAll(/\b\w/g, (char) => char.toUpperCase())
			.trim();
	};

	// Auto-generate columns from query fields and data
	const columns = React.useMemo((): ColumnDef<any>[] => {
		const cols: ColumnDef<any>[] = [];

		// If we have query fields, use them to define columns with proper formatting
		// if (queryFields.length > 0) {
		// 	for (const field of queryFields) {
		// 		cols.push({
		// 			field: field.name as keyof any,
		// 			name: field.displayName,
		// 			width: getColumnWidth(field),
		// 			formatter: getColumnFormatter(field),
		// 		});
		// 	}
		// } else
		if (results.data.length > 0) {
			// Fallback: auto-generate from first data row
			const firstRow = results.data[0];
			for (const key of Object.keys(firstRow)) {
				// Skip columns that are in the excludeColumns array
				if (!excludeColumns.includes(key)) {
					cols.push({
						field: key as keyof any,
						name: formatColumnName(key),
						width: "150px",
					});
				}
			}
		}

		return cols;
	}, [queryFields, results.data, excludeColumns]);

	// Removed unused handlers since we're using SmallPagination now

	if (loading) {
		return (
			<Box
				sx={{
					p: 3,
					textAlign: "center",
					height: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}>
				<CircularProgress />
			</Box>
		);
	}

	if (results.data.length === 0) {
		return (
			<Box sx={{ p: 3, textAlign: "center" }}>
				<Typography
					variant="body2"
					color="text.secondary">
					No data to display
				</Typography>
			</Box>
		);
	}

	return (
		<>
			<Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
				{/* Header (removed fullscreen button) */}

				{/* Table */}
				<Box sx={{ flex: 1, overflow: "hidden" }}>
					<TableContainer
						sx={{
							height: "100%",
							"& .MuiTableCell-root": {
								py: 1,
								px: 2,
								fontSize: "0.875rem",
								borderBottom: "1px solid var(--mui-palette-divider)",
							},
							"& .MuiTableCell-head": {
								py: 0.75,
								px: 2,
								fontSize: "0.8125rem",
								fontWeight: "medium",
								backgroundColor: "var(--mui-palette-background-level1)",
							},
						}}>
						<DataTable
							columns={columns}
							rows={results.data}
							hover
							onClick={onRowClick ? (event, row) => onRowClick(row) : undefined}
							selected={selectedRowId ? new Set([selectedRowId]) : new Set()}
							uniqueRowId={(row) => row.gid || row.id}
						/>
					</TableContainer>
				</Box>

				{/* Pagination - only show if not hiding and has enough data */}
				{!hideInternalPagination && (
					<Box sx={{ pt: 1 }}>
						<SmallPagination
							page={page ?? 1}
							totalPages={totalPages ?? 1}
							onPageChange={(newPage) => onPageChange?.(newPage)}
							compact={true}
						/>
					</Box>
				)}
			</Box>

			{/* Fullscreen Modal */}
			<FullscreenTable
				open={fullscreenOpen}
				onClose={() => setFullscreenOpen(false)}
				results={results}
				queryFields={queryFields}
				onRowClick={onRowClick}
				selectedRowId={selectedRowId}
				excludeColumns={excludeColumns}
			/>
		</>
	);
}
